import type { Trip } from '@/services/trips';

/**
 * Expo Router / navigation search params may pass a value as `string` or `string[]`.
 * Screens that only read `typeof x === 'string'` miss the id and fall back to "default" trips.
 */
export function normalizeTripRouteParam(value: unknown): string | undefined {
  if (value == null) return undefined;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }
  if (Array.isArray(value)) {
    for (const entry of value) {
      if (typeof entry !== 'string') continue;
      const trimmed = entry.trim();
      if (trimmed.length > 0) return trimmed;
    }
  }
  return undefined;
}

const sortByStartDate = (a: Trip, b: Trip) => a.startDate.getTime() - b.startDate.getTime();

export const getDefaultTripId = (trips: Trip[], userId?: string | null): string | null => {
  if (!trips.length) {
    return null;
  }

  const now = new Date();
  const prioritizeOwner = (trip: Trip) => (trip.owner === userId ? 0 : 1);
  const endOfDay = (trip: Trip) => {
    const end = trip.endDate instanceof Date ? new Date(trip.endDate) : new Date(trip.endDate as any);
    if (!Number.isFinite(end.getTime())) {
      return new Date(0);
    }
    end.setHours(23, 59, 59, 999);
    return end;
  };

  const upcomingTrips = trips
    // Consider trips "upcoming/current" through the end of their endDate (inclusive).
    .filter((trip) => endOfDay(trip) >= now)
    .sort((a, b) => {
      const dateDiff = sortByStartDate(a, b);
      if (dateDiff !== 0) {
        return dateDiff;
      }
      return prioritizeOwner(a) - prioritizeOwner(b);
    });

  if (upcomingTrips.length > 0) {
    return upcomingTrips[0].id;
  }

  const personalTrip = trips
    .filter((trip) => trip.owner === userId)
    .sort(sortByStartDate);

  if (personalTrip.length > 0) {
    return personalTrip[0].id;
  }

  const sortedTrips = [...trips].sort(sortByStartDate);
  return sortedTrips[0]?.id ?? null;
};





